import noUiSlider from 'nouislider';

/**
 * Range with steps component
 *
 * @link https://refreshless.com/nouislider/
 */
export default class {
  /**
   * @private
   *
   * @type {HTMLElement}
   */
  #container;

  /**
   * @private
   *
   * @type {HTMLElement}
   */
  #range;

  /**
   * @private
   *
   * @type {HTMLElement}
   */
  #input;

  /**
   * @private
   *
   * @type {Array}
   */
  #data = [];

  /**
   * @param {HTMLElement} container Component container
   */
  constructor(container) {
    this.#container = container;
    if (!this.#container) {
      throw new Error('CVRISK RangeSteps: container is not defined');
    }

    this.#range = this.#container.querySelector('.cvrisk-range');
    this.#input = this.#container.querySelector('.cvrisk-range-value');

    this.#init();
  }

  /**
   * Get range start value from input value
   *
   * @private
   *
   * @return {number} Default 0
   */
  #start() {
    let val = this.#input.value;
    let start = 0;

    if (val && this.#data) {
      for (let i in this.#data) {
        const d = this.#data[i];
        if (d.value === val) {
          start = i;
          break;
        }
      }
    }

    return start;
  }

  /**
   * Get pip label from data
   *
   * @private
   *
   * @param {number} index Data index
   *
   * @return {string}
   */
  #pipLabel(index) {
    index = parseInt(index);
    if (this.#data && index >= 0 && index < this.#data.length) {
      return this.#data[index].label;
    }

    return '';
  }

  /**
   * Get range steps
   *
   * @private
   *
   * @return {object}
   *
   * @see https://refreshless.com/nouislider/slider-values/
   */
  #steps() {
    let steps = {};

    if (this.#data) {
      steps.min = 0;
      steps.max = this.#data.length - 1;

      let step = Math.floor(100 / steps.max);
      let current = 0;
      for (let i = 1; i < steps.max; i++) {
        current += step;
        steps[current.toString() + '%'] = i;
      }
    }

    return steps;
  }

  /**
   * Set range value after clicking a pip
   *
   * @private
   *
   * @param {Event} e Click event
   *
   * @return {void}
   */
  #handlerClickPip(e) {
    var value = Number(e.target.getAttribute('data-value'));
    this.#range.noUiSlider.set(value);
  }

  /**
   * Set input value when updating range
   *
   * @private
   *
   * @return {void}
   */
  #handlerRangeUpdate() {
    let val = this.#range.noUiSlider.get();
    if (val) {
      val = this.#data[parseInt(val)].value;
    }
    this.#input.value = val;
  }

  /**
   * Set data from datalist element
   *
   * @private
   *
   * @return {void}
   */
  #setData() {
    this.#data = [];
    let list = document.getElementById(this.#range.dataset.list);
    if (list) {
      list.querySelectorAll('option').forEach((option) => {
        this.#data.push({
          value: option.value,
          label: option.text,
        });
      });
    }
  }

  /**
   * Initialize data, range and events
   *
   * @private
   *
   * @return {void}
   */
  #init() {
    this.#setData();

    noUiSlider.create(this.#range, {
      start: this.#start(),
      snap: true,
      connect: [true, false],
      animate: true,
      step: 1,
      range: this.#steps(),
      pips: {
        mode: 'steps',
        density: -1,
        format: {
          to: (value) => {
            return this.#pipLabel(value);
          },
        },
      },
    });

    // Update range after clicking a pip
    this.#range.querySelectorAll('.noUi-value').forEach((pip) => {
      pip.addEventListener('click', (e) => this.#handlerClickPip(e));
    });

    // Update input value after changing the range value
    this.#range.noUiSlider.on('update', () => this.#handlerRangeUpdate());
  }

  /**
   * Get input value
   *
   * @return {string}
   */
  getValue() {
    return this.#input.value;
  }

  /**
   * Set range value from a datalist value
   *
   * @param {string} value Datalist value
   *
   * @return {void}
   */
  setRangeValueFromDatalist(value) {
    for (let i in this.#data || {}) {
      const d = this.#data[i];
      if (d.value === value) {
        this.#range.noUiSlider.set(i);
        break;
      }
    }
  }

  /**
   * Set to first value
   *
   * @return {void}
   */
  reset() {
    if (this.#data.length > 0) {
      this.setRangeValueFromDatalist(this.#data[0].value);
    }
  }

  /**
   * @return {void}
   */
  enable() {
    this.#range.removeAttribute('disabled');
  }

  /**
   * @return {void}
   */
  disable() {
    this.#range.setAttribute('disabled', true);
  }
}
